import { useEffect, useState } from "react";
import { QRCodeSVG } from "qrcode.react";
import { GlassButton } from "@/components/glass";
import { TestStatusCard } from "@/components/TestStatusCard";
import "@/styles/security.css";
import "./settings-content.css";
import { getOperator } from "@/lib/auth";
import {
  changeOwnPassword,
  loginActivity,
  mfaDisable,
  mfaEnable,
  mfaSetup,
  mfaStatus,
  type LoginAttempt,
} from "@/lib/api";

// §v3.security — operator self-service Security page, styled in the
// dashboard's Liquid-Glass language (.glass panels + white-alpha type +
// GlassButton), matching Overview / Live Monitor. Sections: account
// summary, two-factor (TOTP) enrollment, change password, and a recent
// sign-in activity logbook (OWASP-recommended audit trail).

type Phase = "loading" | "disabled" | "setup" | "enabled";

const PANEL = "sec-glass rounded-[28px] p-5 sm:p-6";
const INPUT =
  "w-full rounded-xl border border-white/15 bg-white/[0.06] px-3.5 py-2.5 " +
  "text-[14px] text-white placeholder:text-white/35 outline-none " +
  "transition-colors focus:border-white/35 focus:bg-white/[0.09]";
const CODEBOX =
  "select-all overflow-x-auto rounded-xl border border-white/12 bg-black/25 " +
  "p-3 font-mono text-[13px] leading-relaxed text-white/90";
const H2 = "text-[15px] font-semibold tracking-tight text-white/90";
const SUB = "text-[13px] leading-relaxed text-white/55";
const ERR = "text-[13px] text-rose-300";
const OK = "text-[13px] font-medium text-emerald-300";

function fmtTime(unixSeconds: number): string {
  if (!unixSeconds) return "—";
  try {
    return new Date(unixSeconds * 1000).toLocaleString();
  } catch {
    return "—";
  }
}

export function SecurityPage() {
  const operator = getOperator();

  // 2FA
  const [phase, setPhase] = useState<Phase>("loading");
  const [secret, setSecret] = useState("");
  const [otpauthUri, setOtpauthUri] = useState("");
  const [code, setCode] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [backupCodes, setBackupCodes] = useState<string[] | null>(null);
  // Change password
  const [curPw, setCurPw] = useState("");
  const [newPw, setNewPw] = useState("");
  const [confPw, setConfPw] = useState("");
  const [pwBusy, setPwBusy] = useState(false);
  const [pwError, setPwError] = useState<string | null>(null);
  const [pwDone, setPwDone] = useState(false);
  // Recent sign-in activity
  const [activity, setActivity] = useState<LoginAttempt[]>([]);

  useEffect(() => {
    let cancelled = false;
    mfaStatus()
      .then((s) => {
        if (!cancelled) setPhase(s.enabled ? "enabled" : "disabled");
      })
      .catch(() => {
        if (!cancelled) setPhase("disabled");
      });
    loginActivity()
      .then((r) => {
        if (!cancelled) setActivity(r.attempts ?? []);
      })
      .catch(() => {
        /* non-fatal */
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const startSetup = async () => {
    setError(null);
    setBusy(true);
    try {
      const r = await mfaSetup();
      setSecret(r.secret);
      setOtpauthUri(r.otpauth_uri);
      setPhase("setup");
    } catch (e) {
      setError(e instanceof Error ? e.message : "Couldn't start setup.");
    } finally {
      setBusy(false);
    }
  };

  const confirmEnable = async () => {
    setError(null);
    setBusy(true);
    try {
      const r = await mfaEnable(code.trim());
      setBackupCodes(r.backup_codes);
      setCode("");
      setPhase("enabled");
    } catch (e) {
      setError(e instanceof Error ? e.message : "Couldn't enable 2FA.");
    } finally {
      setBusy(false);
    }
  };

  const confirmDisable = async () => {
    setError(null);
    setBusy(true);
    try {
      await mfaDisable(code.trim());
      setCode("");
      setBackupCodes(null);
      setPhase("disabled");
    } catch (e) {
      setError(e instanceof Error ? e.message : "Couldn't disable 2FA.");
    } finally {
      setBusy(false);
    }
  };

  const handleChangePassword = async () => {
    setPwError(null);
    setPwDone(false);
    if (newPw.length < 12) {
      setPwError("New password must be at least 12 characters.");
      return;
    }
    if (newPw !== confPw) {
      setPwError("The new passwords don't match.");
      return;
    }
    setPwBusy(true);
    try {
      await changeOwnPassword(curPw, newPw);
      setPwDone(true);
      setCurPw("");
      setNewPw("");
      setConfPw("");
    } catch (e) {
      setPwError(
        e instanceof Error ? e.message : "Couldn't change your password.",
      );
    } finally {
      setPwBusy(false);
    }
  };

  const groupedSecret = secret.replace(/(.{4})/g, "$1 ").trim();

  const accountRows: Array<[string, string]> = [
    ["Username", operator?.username || "—"],
    ["Email", operator?.email || "—"],
    ["Role", operator?.role_name || "—"],
    ["Workspace", operator?.tenant_id || "—"],
  ];

  return (
    <div className="wn-shell flex-1 overflow-y-auto">
      <div className="space-y-4">
        {/* Header */}
        <div className="wn-head">
          <div className="wn-head-text">
            <h1 className="wn-h1">Security</h1>
            <p className="wn-lede">
              Two-factor authentication, your password, and recent sign-in
              activity for your account.
            </p>
          </div>
        </div>

        {/* Automated testing & deploy health */}
        <TestStatusCard />

        {/* Account */}
        <section className={PANEL}>
          <h2 className={H2}>Account</h2>
          <dl className="mt-4 grid grid-cols-1 gap-x-8 gap-y-3 sm:grid-cols-2">
            {accountRows.map(([label, value]) => (
              <div key={label} className="flex flex-col gap-0.5">
                <dt className="text-[11px] uppercase tracking-wide text-white/40">
                  {label}
                </dt>
                <dd className="truncate text-[14px] text-white/90">{value}</dd>
              </div>
            ))}
          </dl>
        </section>

        {/* Two-factor authentication */}
        <section className={PANEL}>
          <h2 className={H2}>Two-factor authentication (2FA)</h2>
          <p className={`mt-1.5 ${SUB}`}>
            After your password, you'll enter a 6-digit code from an
            authenticator app on your phone — so a stolen password alone can't
            get into your dashboard.
          </p>

          {phase === "loading" ? (
            <p className="mt-4 text-[13px] text-white/40">Loading…</p>
          ) : phase === "enabled" ? (
            <div className="mt-5 space-y-5">
              <p className={OK}>✓ Two-factor authentication is on.</p>
              {backupCodes ? (
                <div className="space-y-2">
                  <p className={SUB}>
                    Save these one-time backup codes somewhere safe — each works
                    once if you lose your phone. They won't be shown again.
                  </p>
                  <pre className={CODEBOX}>{backupCodes.join("\n")}</pre>
                </div>
              ) : null}
              <div className="space-y-3 border-t border-white/10 pt-5">
                <p className="text-[13px] font-medium text-white/85">
                  Turn off 2FA
                </p>
                <p className={SUB}>
                  Enter a current code from your app (or a backup code) to
                  confirm.
                </p>
                <input
                  className={INPUT}
                  value={code}
                  onChange={(e) => setCode(e.target.value)}
                  inputMode="numeric"
                  placeholder="123456 or backup code"
                />
                {error ? <p className={ERR}>{error}</p> : null}
                <GlassButton variant="ghost" loading={busy} onClick={confirmDisable}>
                  Disable 2FA
                </GlassButton>
              </div>
            </div>
          ) : phase === "setup" ? (
            <div className="mt-5 space-y-5">
              <div className="space-y-2">
                <p className={SUB}>
                  1. Scan this QR code with your authenticator app (Google
                  Authenticator, Microsoft Authenticator, or Authy):
                </p>
                <div className="inline-block rounded-xl bg-white p-3">
                  {otpauthUri ? (
                    <QRCodeSVG
                      value={otpauthUri}
                      size={172}
                      bgColor="#ffffff"
                      fgColor="#000000"
                      level="M"
                    />
                  ) : null}
                </div>
              </div>
              <div className="space-y-2">
                <p className={SUB}>
                  Can't scan? Choose{" "}
                  <span className="text-white/85">"Enter a setup key"</span> in
                  your app and type this (account name is your username):
                </p>
                <pre className={CODEBOX}>{groupedSecret}</pre>
              </div>
              <div className="space-y-3 border-t border-white/10 pt-5">
                <label
                  htmlFor="mfa-confirm"
                  className="block text-[13px] font-medium text-white/85"
                >
                  2. Enter the 6-digit code to confirm
                </label>
                <input
                  id="mfa-confirm"
                  className={INPUT}
                  value={code}
                  onChange={(e) => setCode(e.target.value)}
                  inputMode="numeric"
                  placeholder="6-digit code"
                  autoFocus
                />
                {error ? <p className={ERR}>{error}</p> : null}
                <div className="flex gap-2">
                  <GlassButton
                    variant="accent"
                    loading={busy}
                    onClick={confirmEnable}
                  >
                    Confirm &amp; enable
                  </GlassButton>
                  <GlassButton
                    variant="ghost"
                    onClick={() => {
                      setPhase("disabled");
                      setError(null);
                      setCode("");
                    }}
                  >
                    Cancel
                  </GlassButton>
                </div>
              </div>
            </div>
          ) : (
            <div className="mt-5">
              {error ? <p className={`mb-3 ${ERR}`}>{error}</p> : null}
              <GlassButton variant="accent" loading={busy} onClick={startSetup}>
                Enable 2FA
              </GlassButton>
            </div>
          )}
        </section>

        {/* Change password */}
        <section className={PANEL}>
          <h2 className={H2}>Change password</h2>
          <p className={`mt-1.5 ${SUB}`}>
            Choose a strong password — at least 12 characters.
          </p>
          <div className="mt-4 space-y-3">
            {pwDone ? <p className={OK}>✓ Your password has been updated.</p> : null}
            <input
              className={INPUT}
              type="password"
              autoComplete="current-password"
              value={curPw}
              onChange={(e) => setCurPw(e.target.value)}
              placeholder="Current password"
            />
            <input
              className={INPUT}
              type="password"
              autoComplete="new-password"
              value={newPw}
              onChange={(e) => setNewPw(e.target.value)}
              placeholder="New password (at least 12 characters)"
            />
            <input
              className={INPUT}
              type="password"
              autoComplete="new-password"
              value={confPw}
              onChange={(e) => setConfPw(e.target.value)}
              placeholder="Confirm new password"
            />
            {pwError ? <p className={ERR}>{pwError}</p> : null}
            <GlassButton
              variant="accent"
              loading={pwBusy}
              onClick={handleChangePassword}
            >
              Update password
            </GlassButton>
          </div>
        </section>

        {/* Recent sign-in activity */}
        <section className={PANEL}>
          <h2 className={H2}>Recent sign-in activity</h2>
          <p className={`mt-1.5 ${SUB}`}>
            The last few sign-in attempts on your account. If you see something
            you don't recognise, change your password and enable 2FA.
          </p>
          <div className="mt-4 overflow-hidden rounded-xl border border-white/10">
            {activity.length === 0 ? (
              <p className="px-4 py-5 text-[13px] text-white/40">
                No recent sign-in activity recorded yet.
              </p>
            ) : (
              activity.map((a, i) => {
                const ok = a.outcome.toLowerCase().startsWith("succ");
                return (
                  <div
                    key={`${a.at}-${i}`}
                    className="flex items-center justify-between gap-3 border-b border-white/[0.06] px-4 py-2.5 last:border-b-0"
                  >
                    <div className="flex items-center gap-2.5 min-w-0">
                      <span
                        className={`h-2 w-2 shrink-0 rounded-full ${
                          ok ? "bg-emerald-400" : "bg-rose-400"
                        }`}
                      />
                      <span className="truncate text-[13px] text-white/85">
                        {fmtTime(a.at)}
                      </span>
                    </div>
                    <div className="flex items-center gap-3 shrink-0">
                      <span className="font-mono text-[12px] text-white/45">
                        {a.ip || "—"}
                      </span>
                      <span
                        className={`text-[12px] font-medium ${
                          ok ? "text-emerald-300" : "text-rose-300"
                        }`}
                      >
                        {ok ? "Success" : "Failed"}
                      </span>
                    </div>
                  </div>
                );
              })
            )}
          </div>
        </section>
      </div>
    </div>
  );
}

export default SecurityPage;
